/**
 * Entra (Azure AD) Authentication Service
 * Handles Microsoft Entra ID authentication for single-tenant system
 */

import { 
  PublicClientApplication, 
  Configuration, 
  RedirectRequest,
  LogLevel
} from '@azure/msal-browser';
import { log } from '../utils/errors/logger';
import { apiClient } from '../config/apiConfig';

class EntraAuthService {
  private msalInstance: PublicClientApplication | null = null;
  private entraConfig: any = null;
  private initialized = false;
  private initializationPromise: Promise<boolean> | null = null;

  /**
   * Initialize MSAL with Entra configuration from backend
   */
  async initialize(): Promise<boolean> {
    // Return existing initialization if already in progress
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    // Already initialized
    if (this.initialized && this.msalInstance) {
      return true;
    }

    // Start initialization
    this.initializationPromise = this._doInitialize();
    return this.initializationPromise;
  }

  private async _doInitialize(): Promise<boolean> {
    try {
      // Get available auth providers from backend
      const response = await apiClient.getAvailableAuthProvidersApiAuthProvidersGet();
      const providers = response.data.providers || [];
      
      // Find Entra provider
      const entraProvider = providers.find((p: any) => p.provider_type === 'entra');
      
      if (!entraProvider) {
        log.info('Entra authentication not available');
        return false;
      }

      // Get Entra configuration from backend using proper API client
      const configResponse = await apiClient.getEntraConfigApiAuthEntraConfigGet();
      const entraConfigData = configResponse.data;
      
      if (!entraConfigData.tenant_id || !entraConfigData.client_id) {
        throw new Error('Entra configuration incomplete');
      }
      
      const tenantId = entraConfigData.tenant_id;
      const clientId = entraConfigData.client_id;
        
        // Configure MSAL for SPA (Single Page Application)
        const msalConfig: Configuration = {
          auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: `${window.location.origin}/auth/callback`,
            postLogoutRedirectUri: `${window.location.origin}/login`,
            navigateToLoginRequestUrl: false,
          },
          cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false,
          },
          system: {
            loggerOptions: {
              loggerCallback: (level, message, containsPii) => {
                if (containsPii) {return;}
                log.debug(`MSAL [${level}]: ${message}`);
              },
              piiLoggingEnabled: false,
              logLevel: LogLevel.Warning,
            },
          },
        };

        this.msalInstance = new PublicClientApplication(msalConfig);
        await this.msalInstance.initialize();
        
        // Handle any pending redirects from previous attempts
        await this.msalInstance.handleRedirectPromise();
        
        this.entraConfig = entraProvider;
        this.initialized = true;
        
        log.info('MSAL initialized successfully');
        return true;
    } catch (error) {
      log.error('Failed to initialize MSAL', { error });
      this.initializationPromise = null; // Reset on error
      return false;
    } finally {
      this.initializationPromise = null; // Clear the promise
    }
  }

  /**
   * Start login redirect to Microsoft
   */
  async loginRedirect(): Promise<void> {
    if (!this.msalInstance) {
      throw new Error('MSAL not initialized');
    }

    const loginRequest: RedirectRequest = {
      scopes: [
        'openid', 
        'profile', 
        'email', 
        'User.Read',
        'GroupMember.Read.All',  // Required for reading group memberships
        'Group.Read.All',        // Required for reading group details
        'Directory.Read.All'     // Required for directory roles
      ],
      prompt: 'select_account',
      state: 'single-tenant', // Simple state for single-tenant
    };

    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      log.error('Login redirect failed', { error });
      throw error;
    }
  }

  /**
   * Handle redirect promise - call this once at app startup
   */
  async handleRedirectPromise(): Promise<{ access_token: string; refresh_token: string; user: any } | null> {
    if (!this.msalInstance) {
      throw new Error('MSAL not initialized');
    }

    try {
      const response = await this.msalInstance.handleRedirectPromise();
      
      if (!response) {
        return null;
      }

      log.info('MSAL authentication successful', { 
        account: response.account?.username 
      });

      // Exchange MSAL token for backend JWT
      const backendTokens = await this.exchangeTokenWithBackend(
        response.accessToken,
        response.account
      );

      return backendTokens;
    } catch (error) {
      log.error('Handle redirect failed', { error });
      throw error;
    }
  }

  /**
   * Exchange MSAL token for backend JWT
   */
  private async exchangeTokenWithBackend(
    msalToken: string, 
    account: any
  ): Promise<{ access_token: string; refresh_token: string; user: any }> {
    try {
      // Call backend Entra authentication endpoint with MSAL token
      const response = await fetch('/api/auth/entra/token-exchange', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: msalToken,
          user_info: {
            email: account?.username || account?.email,
            name: account?.name,
            oid: account?.localAccountId,
            preferred_username: account?.username,
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Token exchange failed');
      }

      const data = await response.json();
      
      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: {
          id: data.user_id,
          username: data.username,
          email: data.email,
          role: data.role,
          full_name: data.full_name || '',
          email_verified: true,
          needs_admin_approval: false,
        }
      };
    } catch (error) {
      log.error('Token exchange failed', { error });
      throw new Error('Failed to exchange token with backend');
    }
  }

  /**
   * Check if currently in a redirect flow
   */
  isRedirectFlow(): boolean {
    // Check if we have code or error in URL (OAuth2 callback)
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
  }

  /**
   * Clear MSAL cache (for logout)
   */
  async logout(): Promise<void> {
    if (!this.msalInstance) {
      return;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      await this.msalInstance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: `${window.location.origin}/login`,
      });
    }
  }
}

// Create singleton instance
const entraAuthService = new EntraAuthService();
export default entraAuthService;
